import { SdkContext } from '../internal'

/**
 * F-186 Web Push: the caller's devices and push preferences in the community of
 * `X-CommunityId`. Only the owner of a subscription reads, tests or removes it
 * (anybody else's id is a 404).
 */

export type PushPlatform = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'other'

export interface PushConfig {
  /** False when the environment has no VAPID key: no prompt, no card. */
  available: boolean
  publicKey: string | null
  vapidKeyId: string | null
  devices: number
  /** The person already has the installed iPhone/iPad app with push on. */
  hasInstalledIos: boolean
}

export interface PushDevice {
  id: string
  deviceLabel: string
  platform: PushPlatform
  standalone: boolean
  origin: string
  vapidKeyId: string | null
  current?: boolean
  lastSuccessAt: string | null
  createdAt: string
  updatedAt: string
}

export interface PushSubscribeBody {
  endpoint: string
  keys: { p256dh: string; auth: string }
  vapidKeyId?: string
  platform?: PushPlatform
  standalone?: boolean
}

export interface PushCategory {
  key: string
  /** The category's name in the caller's language (F-186 phase 2; absent on older APIs). */
  label?: string
  enabled: boolean
  /** Already sends push: every category the bell feeds (`chat`, `channels`, `oneOnOnes`, `upvotes`, `achievements`, `workflows`, `signupRequests`, `teamNotices`). */
  available: boolean
  defaultEnabled: boolean
}

export interface PushPreferences {
  enabled: boolean
  showPreview: boolean
  enabledCategories: string[]
  categories: PushCategory[]
}

export interface PushPreferencesUpdate {
  enabled?: boolean
  showPreview?: boolean
  enabledCategories?: string[]
}

export interface PushTestResult {
  id: string
  delivered: boolean
  removed: boolean
}

type Headers = Record<string, string>

export class PushResource {
  constructor(private ctx: SdkContext) {}

  async config(): Promise<PushConfig> {
    return this.ctx.call<PushConfig>({ httpMethod: 'GET', path: '/push/config' })
  }

  /** With `endpoint` (this device's), the matching row comes with `current: true`. */
  async listDevices(endpoint?: string): Promise<PushDevice[]> {
    const result = await this.ctx.call<PushDevice[] | { items?: PushDevice[] }>(
      { httpMethod: 'GET', path: '/push/subscriptions' },
      endpoint ? { query: { endpoint } } : {}
    )
    return Array.isArray(result) ? result : result?.items || []
  }

  /** Creates (201) or renews by endpoint (200). */
  async subscribe(body: PushSubscribeBody): Promise<PushDevice> {
    return this.ctx.call<PushDevice>({ httpMethod: 'POST', path: '/push/subscriptions', hasBody: true }, { body })
  }

  /** `headers` lets logout send the token it is about to drop. */
  async removeDevice(id: string, headers?: Headers): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/push/subscriptions/{id}' }, headers ? { id, headers } : { id })
  }

  async testDevice(id: string): Promise<PushTestResult> {
    return this.ctx.call<PushTestResult>({ httpMethod: 'POST', path: '/push/subscriptions/{id}/test', hasBody: true }, { id, body: {} })
  }

  async getPreferences(): Promise<PushPreferences> {
    return this.ctx.call<PushPreferences>({ httpMethod: 'GET', path: '/push/preferences' })
  }

  async updatePreferences(body: PushPreferencesUpdate): Promise<PushPreferences> {
    return this.ctx.call<PushPreferences>({ httpMethod: 'PUT', path: '/push/preferences', hasBody: true }, { body })
  }
}
